// Theme configuration for Splinity
// Each theme defines a complete visual identity — colors, fonts, shapes, AND structural layout

export interface ThemeLayout {
  // Fundamental layout structure
  menuStructure: 'sidebar' | 'topnav' | 'rail';
  // Sidebar shape (attached to edge vs floating with margin)
  sidebarShape: 'attached' | 'floating';
  // Card appearance
  cardStyle: 'bordered' | 'shadow' | 'glass';
  // Button shape
  buttonShape: 'rounded' | 'pill' | 'square';
  // Input appearance
  inputStyle: 'bordered' | 'underline' | 'filled';
  // Sidebar navigation style
  navStyle: 'highlight' | 'pill' | 'indicator';
  // Sidebar appearance
  sidebarStyle: 'solid' | 'colored' | 'transparent';
  // Header bar appearance
  headerStyle: 'standard' | 'accent' | 'transparent';
  // Icon container shape in stat cards
  iconShape: 'rounded' | 'circle' | 'square';
  // Spacing density
  density: 'compact' | 'comfortable' | 'spacious';
  // Page background
  pageBackground: 'flat' | 'gradient' | 'mesh';
  // Icon library
  iconSet: 'lucide' | 'phosphor';
  // Stat summary card treatment (optional — defaults to 'standard' = current gradient cards)
  statCardStyle?: 'standard' | 'tonal' | 'chip' | 'editorial';
  // Operations table treatment (optional — defaults to 'standard'; never alters semantic row colors)
  tableStyle?: 'standard' | 'statement' | 'soft' | 'editorial';
}

// Palette d'un thème — bloc `colors` extrait en type nommé pour permettre la
// surcharge partielle en mode sombre (`colorsDark`).
export interface ThemeColors {
  'primary-50': string;
  'primary-100': string;
  'primary-200': string;
  'primary-300': string;
  'primary-400': string;
  'primary-500': string;
  'primary-600': string;
  'primary-700': string;
  'primary-800': string;
  'primary-900': string;
  'primary-950': string;
  'secondary-50': string;
  'secondary-100': string;
  'secondary-200': string;
  'secondary-300': string;
  'secondary-400': string;
  'secondary-500': string;
  'secondary-600': string;
  'secondary-700': string;
  'secondary-800': string;
  'secondary-900': string;
  'secondary-950': string;
  'bg-base': string;
  'bg-surface': string;
  'bg-surface-hover': string;
  'bg-elevated': string;
  'sidebar-bg'?: string;
  'sidebar-text'?: string;
  'sidebar-text-muted'?: string;
  'sidebar-active-bg'?: string;
  'sidebar-active-text'?: string;
  'sidebar-hover-bg'?: string;
  'sidebar-border'?: string;
  'header-bg'?: string;
  'header-text'?: string;
  'header-border'?: string;
  'text-primary': string;
  'text-secondary': string;
  'text-muted': string;
  'border-default': string;
  'border-light': string;
  'page-gradient-from'?: string;
  'page-gradient-to'?: string;
  // ─── Tokens partenaires / sémantiques (fallbacks dans globals.css) ───
  // Axe partenaires A/B, SÉPARÉ de la sémantique crédit/débit.
  'partner-a'?: string; // accent partenaire 1 (par défaut ≈ primary-600)
  'partner-a-soft'?: string; // teinte douce (fonds de pastille)
  'partner-b'?: string; // accent partenaire 2 (ambre pour Méridien)
  'partner-b-soft'?: string;
  'color-credit'?: string; // vert crédit (tokenisé)
  'color-debit'?: string; // rouge/corail débit (tokenisé)
  // Fond du rail de navigation — volontairement DISTINCT de bg-base :
  // en clair il est plus sourd que la page, en sombre plus profond.
  'bg-rail'?: string;
  // Filet appuyé (séparateurs marqués, bordures de survol)
  'border-strong'?: string;
}

export interface ThemeConfig {
  id: string;
  name: string;
  description: string;
  font: {
    heading: string;
    body: string;
    googleFontsUrl?: string;
  };
  colors: ThemeColors; // palette claire (défaut)
  colorsDark?: Partial<ThemeColors>; // surcharge sombre ; sa présence = thème compatible sombre
  supportsDark?: boolean; // dérivé : true si colorsDark est défini (cf. getTheme)
  style: {
    borderRadius: string;
    borderRadiusLg: string;
    borderRadiusXl: string;
    borderRadiusFull: string;
    shadow: string;
    shadowMd: string;
    shadowLg: string;
  };
  layout: ThemeLayout;
}

// ═══════════════════════════════════════════════════════════════
// THEME 1: CLASSIC — Corporate fintech, clean and professional
// Think: banking app, Stripe dashboard
// ═══════════════════════════════════════════════════════════════
const classic: ThemeConfig = {
  id: 'classic',
  name: 'Classic',
  description: 'Fintech épuré — professionnel, lignes nettes, structure traditionnelle',
  font: {
    heading: "'Inter', system-ui, -apple-system, sans-serif",
    body: "'Inter', system-ui, -apple-system, sans-serif",
  },
  colors: {
    'primary-50': '#eef2ff',
    'primary-100': '#e0e7ff',
    'primary-200': '#c7d2fe',
    'primary-300': '#a5b4fc',
    'primary-400': '#818cf8',
    'primary-500': '#6366f1',
    'primary-600': '#4f46e5',
    'primary-700': '#4338ca',
    'primary-800': '#3730a3',
    'primary-900': '#312e81',
    'primary-950': '#1e1b4b',
    'secondary-50': '#f0fdf4',
    'secondary-100': '#dcfce7',
    'secondary-200': '#bbf7d0',
    'secondary-300': '#86efac',
    'secondary-400': '#4ade80',
    'secondary-500': '#22c55e',
    'secondary-600': '#16a34a',
    'secondary-700': '#15803d',
    'secondary-800': '#166534',
    'secondary-900': '#14532d',
    'secondary-950': '#052e16',
    'bg-base': '#f9fafb',
    'bg-surface': '#ffffff',
    'bg-surface-hover': '#f3f4f6',
    'bg-elevated': '#ffffff',
    'text-primary': '#111827',
    'text-secondary': '#4b5563',
    'text-muted': '#9ca3af',
    'border-default': '#e5e7eb',
    'border-light': '#f3f4f6',
  },
  style: {
    borderRadius: '0.5rem',
    borderRadiusLg: '0.75rem',
    borderRadiusXl: '1rem',
    borderRadiusFull: '9999px',
    shadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    shadowMd: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    shadowLg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  },
  layout: {
    menuStructure: 'sidebar',
    sidebarShape: 'attached',
    cardStyle: 'bordered',
    buttonShape: 'rounded',
    inputStyle: 'bordered',
    navStyle: 'highlight',
    sidebarStyle: 'solid',
    headerStyle: 'standard',
    iconShape: 'rounded',
    density: 'comfortable',
    pageBackground: 'flat',
    iconSet: 'lucide',
  },
};

// ═══════════════════════════════════════════════════════════════
// THEME 2: AURORA — Dark sidebar, premium feel, bold accents
// Think: Linear, Vercel dashboard, modern SaaS
// ═══════════════════════════════════════════════════════════════
const aurora: ThemeConfig = {
  id: 'aurora',
  name: 'Aurora',
  description: 'Premium sombre — sidebar violette, ombres profondes, accents vibrants',
  font: {
    heading: "'Plus Jakarta Sans', system-ui, sans-serif",
    body: "'Plus Jakarta Sans', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap',
  },
  colors: {
    'primary-50': '#faf5ff',
    'primary-100': '#f3e8ff',
    'primary-200': '#e9d5ff',
    'primary-300': '#d8b4fe',
    'primary-400': '#c084fc',
    'primary-500': '#a855f7',
    'primary-600': '#9333ea',
    'primary-700': '#7e22ce',
    'primary-800': '#6b21a8',
    'primary-900': '#581c87',
    'primary-950': '#3b0764',
    'secondary-50': '#fdf4ff',
    'secondary-100': '#fae8ff',
    'secondary-200': '#f5d0fe',
    'secondary-300': '#f0abfc',
    'secondary-400': '#e879f9',
    'secondary-500': '#d946ef',
    'secondary-600': '#c026d3',
    'secondary-700': '#a21caf',
    'secondary-800': '#86198f',
    'secondary-900': '#701a75',
    'secondary-950': '#4a044e',
    'bg-base': '#faf8fc',
    'bg-surface': '#ffffff',
    'bg-surface-hover': '#f5f0fa',
    'bg-elevated': '#ffffff',
    'sidebar-bg': '#1e0a3c',
    'sidebar-text': '#d8b4fe',
    'sidebar-text-muted': '#a78bfa',
    'sidebar-active-bg': 'rgba(168, 85, 247, 0.2)',
    'sidebar-active-text': '#ffffff',
    'sidebar-hover-bg': 'rgba(168, 85, 247, 0.1)',
    'sidebar-border': 'rgba(168, 85, 247, 0.15)',
    'header-bg': '#1e0a3c',
    'header-text': '#e9d5ff',
    'header-border': 'rgba(168, 85, 247, 0.15)',
    'text-primary': '#1c1427',
    'text-secondary': '#57506b',
    'text-muted': '#a098b0',
    'border-default': '#e8e0f0',
    'border-light': '#f3eef8',
    'page-gradient-from': '#faf5ff',
    'page-gradient-to': '#f0e7fe',
  },
  style: {
    borderRadius: '0.75rem',
    borderRadiusLg: '1rem',
    borderRadiusXl: '1.25rem',
    borderRadiusFull: '9999px',
    shadow: '0 1px 3px 0 rgb(88 28 135 / 0.06)',
    shadowMd: '0 4px 16px -2px rgb(88 28 135 / 0.12)',
    shadowLg: '0 12px 32px -4px rgb(88 28 135 / 0.16)',
  },
  layout: {
    menuStructure: 'sidebar',
    sidebarShape: 'floating',
    cardStyle: 'shadow',
    buttonShape: 'pill',
    inputStyle: 'filled',
    navStyle: 'pill',
    sidebarStyle: 'colored',
    headerStyle: 'accent',
    iconShape: 'circle',
    density: 'comfortable',
    pageBackground: 'gradient',
    iconSet: 'phosphor',
  },
};

// ═══════════════════════════════════════════════════════════════
// THEME 9: MÉRIDIEN — direction visuelle par défaut, dark-first
// Fond encre chaude + grain, surfaces séparées par filets, nav en rail,
// équilibre des partenaires en héros. Accent « Encre » bleu royal +
// partenaire B ambre. Seul thème compatible mode sombre (colorsDark).
// ═══════════════════════════════════════════════════════════════
const meridian: ThemeConfig = {
  id: 'meridian',
  name: 'Méridien',
  description: 'Encre chaude dark-first — rail, filets, équilibre des partenaires en héros',
  font: {
    heading: "'Instrument Sans', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    googleFontsUrl:
      'https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&family=Inter:wght@400;500;600;700;800&display=swap',
  },
  colors: {
    // Échelle primaire — Encre (bleu royal)
    'primary-50': '#eef3fc',
    'primary-100': '#d8e4f8',
    'primary-200': '#b3caf1',
    'primary-300': '#86a9e6',
    'primary-400': '#5585d7',
    'primary-500': '#2f66c6',
    'primary-600': '#1E54AC',
    'primary-700': '#1a478f',
    'primary-800': '#183c74',
    'primary-900': '#17345f',
    'primary-950': '#0f1f3d',
    // Échelle secondary — vert (reprise pour compat ; le crédit passe par color-credit)
    'secondary-50': '#e9f9f1',
    'secondary-100': '#c9f0de',
    'secondary-200': '#96e3c1',
    'secondary-300': '#5fd0a2',
    'secondary-400': '#2fb582',
    'secondary-500': '#0E9C67',
    'secondary-600': '#0b8058',
    'secondary-700': '#0a6748',
    'secondary-800': '#0a533b',
    'secondary-900': '#094531',
    'secondary-950': '#04261b',
    // Neutres chauds (clair) — valeurs exactes du prototype « Méridien »
    'bg-base': '#F1EEE7',
    'bg-rail': '#E8E4DA',
    'bg-surface': '#FFFFFF',
    'bg-surface-hover': '#FAF7F1',
    'bg-elevated': '#FFFFFF',
    'text-primary': '#201C17',
    'text-secondary': '#6B655C',
    'text-muted': '#9A9388',
    'border-default': 'rgba(38,32,24,.10)',
    'border-light': 'rgba(38,32,24,.06)',
    'border-strong': 'rgba(38,32,24,.17)',
    // Partenaires / sémantique (clair)
    'partner-a': '#1E54AC',
    'partner-a-soft': '#dce7f8',
    'partner-b': '#C9791F',
    'partner-b-soft': '#f7ecdc',
    'color-credit': '#0E9C67',
    'color-debit': '#D5493D',
  },
  // Surcharge sombre — encre chaude + Encre lumineux + ambre clair
  colorsDark: {
    'primary-400': '#6BA0F0',
    'primary-500': '#5C9CF2',
    'primary-600': '#3F82EA',
    'primary-700': '#2f6bd0',
    'partner-a': '#3F82EA',
    'partner-a-soft': 'rgba(63,130,234,.16)',
    'partner-b': '#E8A24A',
    'partner-b-soft': 'rgba(232,162,74,.16)',
    'color-credit': '#46D9A0',
    'color-debit': '#FF7E6F',
    'bg-base': '#100E0C',
    'bg-rail': '#0A0908',
    'bg-surface': '#17150F',
    'bg-surface-hover': '#201D16',
    'bg-elevated': '#201D16',
    'text-primary': '#F0EBE1',
    'text-secondary': '#ABA396',
    'text-muted': '#6F695E',
    'border-default': 'rgba(255,249,236,.085)',
    'border-light': 'rgba(255,249,236,.05)',
    'border-strong': 'rgba(255,249,236,.16)',
  },
  style: {
    // Rayons du prototype : r-sm 10px / r 14px / r-lg 20px (panneaux) / pilule.
    borderRadius: '10px',
    borderRadiusLg: '14px',
    borderRadiusXl: '20px',
    borderRadiusFull: '999px',
    // Ombre « papier » du prototype (double couche : contact + diffusion)
    shadow: '0 1px 2px rgba(38,32,24,.04), 0 12px 32px -14px rgba(38,32,24,.14)',
    shadowMd: '0 1px 2px rgba(38,32,24,.05), 0 18px 44px -18px rgba(38,32,24,.18)',
    shadowLg: '0 1px 2px rgba(38,32,24,.06), 0 30px 70px -22px rgba(38,32,24,.26)',
  },
  layout: {
    menuStructure: 'rail',
    sidebarShape: 'attached',
    cardStyle: 'bordered',
    buttonShape: 'rounded',
    inputStyle: 'bordered',
    navStyle: 'highlight',
    sidebarStyle: 'solid',
    headerStyle: 'transparent',
    iconShape: 'rounded',
    density: 'comfortable',
    pageBackground: 'flat',
    iconSet: 'lucide',
    // Visage « éditorial » sur les stat cards + « relevé » sur le tableau d'opérations
    statCardStyle: 'editorial',
    tableStyle: 'statement',
  },
};

export const themes: Record<string, ThemeConfig> = {
  meridian,
  classic,
  aurora,
};

export const DEFAULT_THEME_ID = 'meridian';

export function getTheme(id: string): ThemeConfig {
  const theme = themes[id] || themes[DEFAULT_THEME_ID];
  // supportsDark est dérivé : un thème est compatible sombre s'il définit colorsDark.
  return { ...theme, supportsDark: !!theme.colorsDark };
}

export function getThemeList(): ThemeConfig[] {
  return Object.values(themes).map((t) => ({ ...t, supportsDark: !!t.colorsDark }));
}
